import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaFileUpload, FaTimes } from "react-icons/fa";
import "../styles/ContactUs.css";
import MapLocation from "./MapLocation";
import CompanyProfile from "./CompanyProfile";

function ContactUs() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    mobile: "",
    location: "",
    numPeople: "",
    message: "",
  });

  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (selectedFile) {
      // Check file size (5MB limit)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setStatus({
          type: "error",
          message: "File size must be less than 5MB. Please choose a smaller file."
        });
        return;
      }

      setFile(selectedFile);

      // Create preview URL based on file type
      const fileType = selectedFile.type;
      if (fileType.startsWith('image/') || fileType === 'application/pdf') {
        const previewUrl = URL.createObjectURL(selectedFile);
        setFilePreviewUrl(previewUrl);
      } else {
        setFilePreviewUrl(null);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const renderFilePreview = () => {
    if (!file) return null;

    const fileType = file.type;

    return (
      <div className="app-file-preview">
        <div className="app-file-preview__header">
          <p className="app-file-preview__title">File Preview</p>
          <button 
            type="button" 
            className="app-file-preview__remove-btn"
            onClick={removeFile}
            aria-label="Remove file"
          >
            <FaTimes />
          </button>
        </div>
        
        {fileType.startsWith('image/') && filePreviewUrl && (
          <div className="app-file-preview__image-container">
            <img 
              src={filePreviewUrl} 
              alt="File preview" 
              className="app-file-preview__image"
            />
          </div>
        )}
        
        {fileType === 'application/pdf' && filePreviewUrl && (
          <div className="app-file-preview__pdf-container">
            <iframe 
              src={filePreviewUrl} 
              title="PDF Preview" 
              className="app-file-preview__pdf"
            />
          </div>
        )}
        
        {!fileType.startsWith('image/') && fileType !== 'application/pdf' && (
          <div className="app-file-preview__file-info">
            <span className="app-file-preview__file-icon">
              {getFileIcon(fileType)}
            </span>
            <div className="app-file-preview__file-details">
              <p className="app-file-preview__file-name">{file.name}</p>
              <p className="app-file-preview__file-size">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Append form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      // Append file if exists
      if (file) {
        submitData.append('file', file);
      }

      // const response = await fetch("http://localhost:5000/send", {
      const response = await fetch("https://laxmi-hospitality-services-backend.onrender.com/send", {
        method: "POST",
        body: submitData, // Don't set Content-Type header for FormData
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: "success", message: data.message });
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          companyName: "",
          mobile: "",
          location: "",
          numPeople: "",
          message: "",
        });
        removeFile();
        setAttemptCount(0);
      } else {
        throw new Error(data.message || "Failed to send inquiry");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);

      if (newAttemptCount >= 2) {
        setStatus({
          type: "error",
          message: (
            <div>
              <p>We're experiencing technical difficulties. Please contact us directly:</p>
              <div className="app-contact-fallback">
                <p><strong>📞 Phone:</strong> +91 99875 58691 / +91 97249 82104</p>
                <p><strong>📧 Email:</strong> laxmihospitalityservicesind@gmail.com</p>
              </div>
            </div>
          )
        });
      } else {
        setStatus({
          type: "error",
          message: "Failed to send message. Please try again.  | Server Error. "
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
<>
    <Helmet>
     <title>Contact Us | Partner with Laxmi Hospitality for Industrial Catering</title>
     <meta
       name="description"
       content="Partner with us for fully customized, large-scale industrial catering across India. Contact Laxmi Hospitality to discuss flexible, contract-based solutions that deliver nourishing meals tailored to your business needs."
     />
     <meta name="robots" content="index, follow" />
     <meta name="viewport" content="width=device-width, initial-scale=1.0" />
     <link rel="canonical" href="https://laxmihospitalityservices.in/contactus" />
   
     {/* -------- OPEN GRAPH (SOCIAL) -------- */}
     <meta property="og:type"        content="website" />
     <meta property="og:url"         content="https://laxmihospitalityservices.in/contactus" />
     <meta property="og:title"       content="Contact Us | Partner with Laxmi Hospitality for Industrial Catering" />
     <meta property="og:description" content="Partner with us for customized industrial catering across India. Contact us to discuss flexible solutions for your business needs." />
     <meta property="og:site_name"   content="Laxmi Hospitality Services" />
     <meta property="og:locale"      content="en_IN" />
   
     {/* -------- CONTACT PAGE LINKED DATA-------- */}
     <script type="application/ld+json">
     {`
       {
         "@context": "https://schema.org",
         "@type": "ContactPage",
         "@id": "https://laxmihospitalityservices.in/contactus",
         "name": "Contact Laxmi Hospitality Services",
         "description": "Get in touch with Laxmi Hospitality Services for customized industrial catering solutions across India. Partner with us for flexible, contract-based meal services.",
         "url": "https://laxmihospitalityservices.in/contactus",
         "about": { "@id": "https://laxmihospitalityservices.in/#organization" },
         "publisher": { "@id": "https://laxmihospitalityservices.in/#organization" },
         "mainEntity": {
           "@type": "Organization",
           "@id": "https://laxmihospitalityservices.in/#organization",
           "name": "Laxmi Hospitality Services",
           "address": {
             "@type": "PostalAddress",
             "streetAddress": "Evershine Sapphire, Above SBI Bank, DP Road 9, Chandivali, Powai",
             "addressLocality": "Mumbai",
             "postalCode": "400072",
             "addressRegion": "Maharashtra",
             "addressCountry": "IN"
           },
           "contactPoint": [
             {
               "@type": "ContactPoint",
               "telephone": "+91-9724982104",
               "contactType": "customer service",
               "areaServed": "IN",
               "availableLanguage": ["English", "Hindi", "Marathi"]
             },
             {
               "@type": "ContactPoint",
               "telephone": "+91-9987558691",
               "contactType": "administrator",
               "areaServed": "IN",
               "availableLanguage": ["English", "Hindi"]
             }
           ],
           "email": [
             "sadanandgshetty185@gmail.com",
             "laxmihospitalityservicesind@gmail.com"
           ]
         },
         "sameAs": [
           "https://www.linkedin.com/company/laxmi-hospitality-services/",
           "https://www.instagram.com/laxmi_hospitality_services/"
         ],
         "inLanguage": "en-IN"
       }
     `}
     </script>
   
     {/* -------- CONTACT PAGE BREADCRUMB -------- */}
     <script type="application/ld+json">
     {`
       {
         "@context": "https://schema.org",
         "@type": "BreadcrumbList",
         "@id": "https://laxmihospitalityservices.in/#breadcrumb",
         "name": "Website Breadcrumb",
         "itemListElement": [
           { "@type": "ListItem", "position": 1, "name": "Home",       "item": "https://laxmihospitalityservices.in/" },
           { "@type": "ListItem", "position": 2, "name": "Contact Us", "item": "https://laxmihospitalityservices.in/contactus" }
         ]
       }
     `}
     </script>
   </Helmet>

    <div className="app-contact-us__container">
      <div className="app-contact-us__banner">
        <img
          src={"/images/Contact-Banner.png"}
          alt="Delicious food spread banner"
          className="app-contact-us__banner-image"
        />
        <div className="app-contact-us__banner-overlay">
          <h1 className="app-contact-us__banner-title">Contact Us</h1>
          <p className="app-contact-us__banner-breadcrumb">
            <Link to="/" className="app-contact-us__breadcrumb-link">
              Home
            </Link>{" "}
            / Contact Us
          </p>
        </div>
      </div>

      <div className="app-contact-us__content-section">
        <p className="app-contact-us__greeting">HELLO EVERYONE</p>
        <h2 className="app-contact-us__main-heading">
          Specializing in industrial catering, we deliver fully customized,
           large-scale catering for your business needs.
        </h2>
        <p className="app-contact-us__description">
       We focus on providing consistent, high-quality catering through flexible contract-based services,
        ensuring your employees receive nourishing and satisfying meals.
         From diverse menus to specific dietary requirements, our solutions are designed to align perfectly with your operational demands and preferences.
          Partner with us to fulfill your company's unique catering needs with reliability and excellence. 
        </p>
        <a href="#foodTastingForm" className="app-contact-us__book-now-button">
          Book Now <span className="app-contact-us__book-now-arrow">→</span>
        </a>
      </div>

      <div className="app-food-tasting__section" id="foodTastingForm">
        <div className="app-food-tasting__header">
          <h2 className="app-food-tasting__title">
            Connect With Us to Customize Your Industrial Catering !!
          </h2>
        </div>
        
        <form className="app-food-tasting__form" onSubmit={handleSubmit}>
          {/* Name Fields */}
          <div className="app-food-tasting__form-group app-food-tasting__form-group--name">
            <div className="app-food-tasting__input-wrapper">
              <label htmlFor="firstName" className="app-food-tasting__label">
                First Name<span className="app-food-tasting__required-star">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                placeholder="First Name"
                required
                className="app-food-tasting__input"
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            <div className="app-food-tasting__input-wrapper">
              <label
                htmlFor="lastName"
                className="app-food-tasting__label app-food-tasting__label--hidden"
              >
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Last Name"
                className="app-food-tasting__input"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Email */}
          <div className="app-food-tasting__form-group">
            <label htmlFor="email" className="app-food-tasting__label">
              Email<span className="app-food-tasting__required-star">*</span>
            </label>
            <input
            
              type="email"
              id="email"
              name="email"
              placeholder="Enter your Email"
              required
              className="app-food-tasting__input"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          {/* Company Name */}
          <div className="app-food-tasting__form-group">
            <label htmlFor="companyName" className="app-food-tasting__label">
              Company Name
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              placeholder="Your company or organization"
              className="app-food-tasting__input"
              value={formData.companyName}
              onChange={handleChange}
            />
          </div>

          {/* Mobile */}
          <div className="app-food-tasting__form-group">
            <label htmlFor="mobile" className="app-food-tasting__label">
              Mobile<span className="app-food-tasting__required-star">*</span>
            </label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              placeholder="Enter your Phone Number"
              required
              className="app-food-tasting__input"
              value={formData.mobile}
              onChange={handleChange}
            />
          </div>

          {/* Location */}
          <div className="app-food-tasting__form-group">
            <label htmlFor="location" className="app-food-tasting__label">
              Location<span className="app-food-tasting__required-star">*</span>
            </label>
            <input
              type="text"
              id="location"
              name="location"
              placeholder="Enter your location"
              required
              className="app-food-tasting__input"
              value={formData.location}
              onChange={handleChange}
            />
          </div>

          {/* Number of People */}
          <div className="app-food-tasting__form-group">
            <label htmlFor="numPeople" className="app-food-tasting__label">
              Approximate No. of People
              <span className="app-food-tasting__required-star">*</span>
            </label>
            <div className="app-food-tasting__select-wrapper">
              <select
                id="numPeople"
                name="numPeople"
                required
                className="app-food-tasting__select"
                value={formData.numPeople}
                onChange={handleChange}
              >
                <option value="">Select an option</option>
                <option value="1-50"> 1 - 50</option>
                <option value="51-100">51 - 100</option>
                <option value="101-200">101 - 200</option>
                <option value="201-300">201 - 300</option>
                <option value="301-400">301 - 400</option>
                <option value="401-500">401 - 500</option>
                <option value="501-600">501 - 600</option>
                <option value="601-1000">601 - 1000</option>
                <option value="1000+">1000+</option>
              </select>
            </div>
          </div>

          {/* Message */}
          <div className="app-food-tasting__form-group">
            <label htmlFor="message" className="app-food-tasting__label">
              Message<span className="app-food-tasting__required-star">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows="5"
              placeholder="Tell us about your  requirements, preferred menu, dietary restrictions, or any special requests or any other detailed query!"
              required
              className="app-food-tasting__textarea"
              value={formData.message}
              onChange={handleChange}
            />
          </div>

          {/* File Upload */}
          <div className="app-food-tasting__form-group">
            <label htmlFor="fileUpload" className="app-food-tasting__label">
              <FaFileUpload className="app-file-upload__icon" />
              Attach File (Optional)
            </label>
            <div className="app-file-upload__container">
              <input
                type="file"
                id="fileUpload"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="app-file-upload__input"
              />
              <label htmlFor="fileUpload" className="app-file-upload__label">
                <FaFileUpload className="app-file-upload__icon" />
                <span className="app-file-upload__text">
                  {file ? file.name : "Choose file or drag & drop"}
                </span>
                <span className="app-file-upload__subtext">
                  Max size: 5MB
                </span>
              </label>
            </div>
            
            {/* File Preview */}
            {renderFilePreview()}
          </div>

          {/* Status Message */}
          {status && (
            <div className={`app-form-status app-form-status--${status.type}`}>
              {typeof status.message === 'string' ? status.message : status.message}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="app-food-tasting__submit-button"
            disabled={loading}
          >
            {loading ? "Sending...Kindly wait for 20-30 seconds" : "SEND INQUIRY"}
          </button>
        </form>
      </div>

      <MapLocation />
      <CompanyProfile />
      
    </div>
    </>
  );
}

export default ContactUs;